// CommunityAppCard - Card for community browse grid
// Displays app name, description, owner, views, likes, trending score, tags.
// Non-admin: click opens playspace. Admin: click opens detail modal.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import './CommunityAppCard.css';

function truncateDescription(text, maxLen = 100) {
  if (!text) return '';
  const plain = String(text)
    .replace(/#{1,6}\s/g, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/\n/g, ' ')
    .trim();
  return plain.length > maxLen ? `${plain.substring(0, maxLen)}...` : plain;
}

export function CommunityAppCard({ app, isAdmin = false, onAdminCardClick }) {
  const navigate = useNavigate();

  if (!app) return null;

  const name = app.name || 'Untitled';
  const description = truncateDescription(app.description);
  const ownerUsername = app.ownerUsername || '';
  const views = app.views ?? 0;
  const likes = app.likes ?? 0;
  const trendingScore = app.trendingScore;
  const tags = Array.isArray(app.tags) ? app.tags : [];

  const appColor = app.settings?.appColor;
  const cardTintStyle = appColor
    ? {
        '--app-color-r': parseInt(appColor.slice(1, 3), 16),
        '--app-color-g': parseInt(appColor.slice(3, 5), 16),
        '--app-color-b': parseInt(appColor.slice(5, 7), 16),
        '--app-color-alpha': appColor.length === 9
          ? parseInt(appColor.slice(7, 9), 16) / 255
          : 0.25,
        '--has-app-color': 1,
      }
    : { '--has-app-color': 0 };

  const playspaceHref = `/playspace/${app.id}?mode=sharing`;

  const handleClick = (e) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return;
    }
    e.preventDefault();
    if (isAdmin && onAdminCardClick) {
      onAdminCardClick(app);
    } else {
      navigate(playspaceHref);
    }
  };

  const Wrapper = isAdmin && onAdminCardClick ? 'div' : 'a';
  const wrapperProps = isAdmin && onAdminCardClick
    ? { className: 'community-app-card', style: cardTintStyle, onClick: handleClick, role: 'button', tabIndex: 0, onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e); } } }
    : { href: playspaceHref, className: 'community-app-card', style: cardTintStyle, onClick: handleClick, 'data-navigation-button': true };

  return (
    <Wrapper {...wrapperProps}>
      <div className="community-app-card__icon">
        <i className="fa-solid fa-cube" aria-hidden />
      </div>
      <div className="community-app-card__details">
        <div className="community-app-card__content">
          <h3 className="community-app-card__title">{name}</h3>
          {description && (
            <p className="community-app-card__description">{description}</p>
          )}
          {tags.length > 0 && (
            <div className="community-app-card__tags">
              {tags.slice(0, 3).map((tag) => (
                <span key={tag} className="community-app-card__tag">
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="community-app-card__tag community-app-card__tag--more">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="community-app-card__meta">
          {ownerUsername && (
            <span className="community-app-card__owner">
              <i className="fa-solid fa-user" aria-hidden /> {ownerUsername}
            </span>
          )}
          <span className="community-app-card__views">
            <i className="fa-solid fa-eye" aria-hidden /> {views}
          </span>
          <span className="community-app-card__likes">
            <i className="fa-solid fa-heart" aria-hidden /> {likes}
          </span>
          {isAdmin && trendingScore != null && (
            <span className="community-app-card__trending" title="Trending score">
              <i className="fa-solid fa-chart-line" aria-hidden />{' '}
              {Number(trendingScore).toFixed(1)}
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
